/**
 * Tiny in-app toast system - zero deps, ~150 LOC.
 *
 * Usage:
 *   1. Wrap the app once with <ToastProvider /> (already done in main.tsx).
 *   2. Anywhere in the component tree: `const toast = useToast();`
 *      then call `toast.success("Saved")`, `toast.error("Boom")`,
 *      `toast.info("Heads up")` or `toast.warn("Careful")`.
 *
 * Toasts auto-dismiss after 4s by default; click the × to dismiss manually.
 * Stacked top-right. Survives navigation.
 */

import {
  createContext, useContext, useState, useCallback, useEffect,
  type ReactNode,
} from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { cn } from "@/lib/cn";

type ToastTone = "success" | "error" | "warn" | "info";

interface ToastItem {
  id: string;
  tone: ToastTone;
  message: string;
  /** Optional short subtitle (≤120 chars). */
  detail?: string;
  /** Milliseconds before auto-dismiss. 0 = sticky. Default 4000. */
  duration?: number;
}

interface ToastApi {
  success: (message: string, detail?: string) => void;
  error:   (message: string, detail?: string) => void;
  warn:    (message: string, detail?: string) => void;
  info:    (message: string, detail?: string) => void;
  /** Lower-level: full control. */
  push: (input: Omit<ToastItem, "id">) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_STYLES: Record<ToastTone, { icon: typeof CheckCircle2; cls: string }> = {
  success: { icon: CheckCircle2,   cls: "bg-green-50 border-green-200 text-green-900" },
  error:   { icon: AlertCircle,    cls: "bg-red-50 border-red-200 text-red-900" },
  warn:    { icon: AlertTriangle,  cls: "bg-amber-50 border-amber-200 text-amber-900" },
  info:    { icon: Info,           cls: "bg-slate-50 border-slate-200 text-slate-900" },
};

let counter = 0;
function nextId(): string {
  counter++;
  return `t-${Date.now()}-${counter}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((input: Omit<ToastItem, "id">) => {
    const id = nextId();
    const item: ToastItem = { id, duration: 4000, ...input };
    setItems((cur) => [...cur, item]);
    if (item.duration && item.duration > 0) {
      setTimeout(() => dismiss(id), item.duration);
    }
  }, [dismiss]);

  const api: ToastApi = {
    push,
    success: (message, detail) => push({ tone: "success", message, detail }),
    error:   (message, detail) => push({ tone: "error",   message, detail, duration: 6000 }),
    warn:    (message, detail) => push({ tone: "warn",    message, detail }),
    info:    (message, detail) => push({ tone: "info",    message, detail }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRack items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastRack({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {items.map((t) => (
        <ToastView key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    // Trigger enter animation on next frame.
    const r = requestAnimationFrame(() => setEnter(true));
    return () => cancelAnimationFrame(r);
  }, []);
  const tone = TONE_STYLES[item.tone];
  const Icon = tone.icon;
  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto rounded-lg border shadow-md px-3 py-2.5",
        "transition-all duration-200 ease-out",
        enter ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4",
        tone.cls,
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-snug">{item.message}</div>
          {item.detail && (
            <div className="text-xs opacity-80 mt-0.5 break-words">{item.detail}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="opacity-60 hover:opacity-100 transition-opacity shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fail soft - return a no-op API so consumers don't crash if the
    // provider is missing (e.g. during tests).
    return {
      push: () => {},
      success: () => {},
      error: () => {},
      warn: () => {},
      info: () => {},
    };
  }
  return ctx;
}
